import { useEffect, useState } from "react";
import { toast } from "sonner";
import { AdminGuard, EmailList } from "@/components/admin/AdminGuard";
import { errText, torontoMonth } from "@/lib/api";
import { adminAction, adminSubs, authed , adminMonth, adminNotifyCrew, adminSetMonth } from "@/lib/admin";

const STATUSES = ["all", "pending", "approved", "rejected", "winner"];
const COLOURS = [{ name: "Cloud", hex: "#F4F3EC" }, { name: "Sus Black", hex: "#111111" }, { name: "White", hex: "#FFFFFF" }, { name: "Olive", hex: "#5B5B3A" }];
const field = "border-b border-ink/40 bg-transparent py-2 font-mono text-sm focus:border-ink focus:outline-none";

const WinnerForm = ({ sub, onDone }) => {
  const [why, setWhy] = useState("");
  const [price, setPrice] = useState(49);
  const [colors, setColors] = useState(["Cloud"]);
  const toggle = (n) => setColors((c) => (c.includes(n) ? c.filter((x) => x !== n) : [...c, n]));
  const submit = async () => {
    try {
      const r = await adminAction(sub.id, "winner", { why_note: why, price: Number(price), colors: COLOURS.filter((c) => colors.includes(c.name)) });
      toast.success(`Winner published. Tee created: ${r.product_slug}`);
      onDone();
    } catch (e) { toast.error(errText(e)); }
  };
  return (
    <div className="mt-5 space-y-4 border border-ink/15 bg-white p-5" data-testid="admin-winner-form">
      <label className="block"><span className="label text-ink/55">Why we picked it</span>
        <textarea value={why} onChange={(e) => setWhy(e.target.value)} rows={3} className={`${field} w-full`} data-testid="admin-winner-why" /></label>
      <label className="block"><span className="label text-ink/55">Tee price (CAD)</span>
        <input type="number" value={price} onChange={(e) => setPrice(e.target.value)} className={`${field} w-32`} data-testid="admin-winner-price" /></label>
      <div><span className="label text-ink/55">Colours</span>
        <div className="mt-2 flex flex-wrap gap-3">{COLOURS.map((c) => (
          <label key={c.name} className="flex items-center gap-2 text-sm"><input type="checkbox" checked={colors.includes(c.name)} onChange={() => toggle(c.name)} data-testid={`admin-winner-color-${c.name.toLowerCase().replace(/\s+/g, "-")}`} />{c.name}</label>
        ))}</div></div>
      <button onClick={submit} disabled={why.trim().length < 3 || !colors.length} className="btn btn-lime" data-testid="admin-winner-confirm">Publish winner + create tee</button>
    </div>
  );
};

const Row = ({ s, reload }) => {
  const [open, setOpen] = useState(false);
  const [picking, setPicking] = useState(false);
  const [rejecting, setRejecting] = useState(false);
  const [notify, setNotify] = useState(true);
  const act = async (a, body) => { try { await adminAction(s.id, a, body); toast.success(a === "unpublish" ? "Winner unpublished, tee removed" : `Submission ${a}d`); reload(); } catch (e) { toast.error(errText(e)); } };
  return (
    <li className="border-b border-ink/10 py-5" data-testid={`admin-sub-${s.id}`}>
      <div className="flex flex-wrap items-center gap-5">
        <img src={authed(s.design_url)} alt={s.design_title} className="tile h-16 w-16 object-cover" />
        <div className="min-w-[200px] flex-1">
          <p className="font-medium">{s.design_title} {s.is_seed && <span className="label ml-2 text-ink/40">seed</span>}</p>
          <p className="text-[13px] text-ink/60">{s.full_name}, {s.age} · {s.city} · {s.month}</p>
        </div>
        <span className={`label font-bold ${s.status === "winner" ? "bg-lime px-2" : s.status === "rejected" ? "text-alert" : ""}`} data-testid={`admin-status-${s.id}`}>{s.status}</span>
        <span className="label w-20 text-right" data-testid={`admin-votes-${s.id}`}>{s.votes} votes</span>
        <div className="flex gap-3">
          <button onClick={() => setOpen(!open)} className="link-u" data-testid={`admin-view-${s.id}`}>{open ? "Hide" : "View"}</button>
          {s.status !== "approved" && s.status !== "winner" && <button onClick={() => act("approve")} className="link-u" data-testid={`admin-approve-${s.id}`}>Approve</button>}
          {s.status !== "rejected" && s.status !== "winner" && <button onClick={() => setRejecting(!rejecting)} className="link-u text-alert" data-testid={`admin-reject-${s.id}`}>Reject</button>}
          {s.status === "approved" && <button onClick={() => { setOpen(true); setPicking(true); }} className="link-u" data-testid={`admin-winner-${s.id}`}>Pick as winner</button>}
          {s.status === "winner" && <button onClick={() => window.confirm("Unpublish this winner and remove its tee from the shop?") && act("unpublish")} className="link-u text-alert" data-testid={`admin-unpublish-${s.id}`}>Unpublish winner</button>}
        </div>
      </div>
      {rejecting && s.status !== "rejected" && s.status !== "winner" && (
        <div className="mt-4 flex flex-wrap items-center gap-5 bg-pink/40 px-4 py-3" data-testid={`admin-reject-panel-${s.id}`}>
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={notify} onChange={(e) => setNotify(e.target.checked)} data-testid={`admin-reject-email-${s.id}`} /> Send a kind "not this time" email{s.parent_email && s.age < 16 ? " (parent CC'd)" : ""}</label>
          <button onClick={() => { setRejecting(false); act("reject", { send_email: notify }); }} className="btn btn-ink !h-10" data-testid={`admin-reject-confirm-${s.id}`}>Confirm reject</button>
        </div>
      )}
      {open && (
        <div className="mt-5 grid gap-6 md:grid-cols-12" data-testid={`admin-detail-${s.id}`}>
          <div className="flex gap-4 md:col-span-4">
            <img src={authed(s.design_url)} alt="" className="tile w-1/2 object-contain" />
            {s.photo_url && <img src={authed(s.photo_url)} alt="" className="w-1/2 rounded-[16px] object-cover" />}
          </div>
          <dl className="grid grid-cols-[140px_1fr] gap-y-2 text-sm md:col-span-8">
            <dt className="label text-ink/50">Email</dt><dd>{s.email}</dd>
            <dt className="label text-ink/50">Instagram</dt><dd>{s.instagram ? `@${s.instagram}` : "None"}</dd>
            <dt className="label text-ink/50">Parent email</dt><dd data-testid={`admin-parent-${s.id}`}>{s.parent_email || "Not required (16+)"}{s.parent_email && (s.parent_approved ? " · approved" : " · NOT approved")}</dd>
            <dt className="label text-ink/50">Story</dt><dd className="leading-relaxed">{s.story}</dd>
            {s.why_note && <><dt className="label text-ink/50">Why we picked it</dt><dd>{s.why_note}</dd></>}
            <dt className="label text-ink/50">Emails</dt><dd><EmailList emails={s.emails} testid={`admin-emails-${s.id}`} /></dd>
            {s.product_slug && <><dt className="label text-ink/50">Product</dt><dd><a href={`/product/${s.product_slug}`} className="underline">{s.product_slug}</a></dd></>}
          </dl>
          {picking && s.status === "approved" && <div className="md:col-span-12"><WinnerForm sub={s} onDone={reload} /></div>}
        </div>
      )}
    </li>
  );
};

const MonthPanel = ({ month }) => {
  const [m, setM] = useState(null);
  const [date, setDate] = useState("");
  const load = () => adminMonth(month).then((r) => { setM(r); setDate(toLocal(r.close_at)); }).catch(() => setM(null));
  useEffect(() => { if (month) load(); }, [month]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!m) return null;
  const save = async (val) => { try { const r = await adminSetMonth(month, val); setM(r); setDate(toLocal(r.close_at)); toast.success("Voting close date saved"); } catch (e) { toast.error(errText(e)); } };
  const crew = async () => {
    if (!window.confirm("Email every Sus Crew subscriber about this month's winner?")) return;
    try { const r = await adminNotifyCrew(month); toast.success(`Queued ${r.queued} emails (${r.already_sent} already sent)`); setTimeout(load, 4000); } catch (e) { toast.error(errText(e)); }
  };
  return (
    <div className="mt-8 grid gap-6 border-[1.5px] border-ink bg-sky/30 p-5 lg:grid-cols-12" data-testid="admin-month-panel">
      <div className="lg:col-span-4">
        <p className="label font-bold">Voting · {month}</p>
        <p className={`sticker mt-3 ${m.voting_open ? "bg-lime" : "bg-pink"}`} data-testid="admin-month-status">{m.voting_open ? "Open" : "Closed"}</p>
        <p className="mt-3 text-[13px] text-ink/70">Default close: last day of the month, 23:59:59 Toronto.</p>
      </div>
      <div className="flex flex-wrap items-end gap-4 lg:col-span-5">
        <label><span className="label text-ink/60">Voting closes (your time)</span><input type="datetime-local" step="1" value={date} onChange={(e) => setDate(e.target.value)} className="field font-mono text-sm" data-testid="admin-month-close" /></label>
        <button onClick={() => save(new Date(date).toISOString())} className="btn btn-ink !h-10" data-testid="admin-month-save">Save</button>
        {m.overridden && <button onClick={() => save(null)} className="link-u" data-testid="admin-month-reset">Reset to month end</button>}
      </div>
      <div className="lg:col-span-3 lg:text-right">
        {m.winner_slug ? <button onClick={crew} className="btn btn-pink !h-10" data-testid="admin-notify-crew">Notify crew</button> : <p className="text-[13px] text-ink/60">Pick a winner to notify the crew.</p>}
        {(m.crew_notified > 0 || m.crew_failed > 0) && <p className="label mt-3 !text-[10px]" data-testid="admin-crew-stats">{m.crew_notified} sent · {m.crew_failed} failed</p>}
      </div>
    </div>
  );
};

const pad = (n) => String(n).padStart(2, "0");
const toLocal = (iso) => { const d = new Date(iso); return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`; };

const Panel = () => {
  const [month, setMonth] = useState(torontoMonth());
  const [status, setStatus] = useState("all");
  const [subs, setSubs] = useState(null);
  const load = () => adminSubs({ month: month || undefined, status }).then(setSubs).catch((e) => toast.error(errText(e)));
  useEffect(() => { load(); }, [month, status]); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <>
      <div className="flex flex-wrap items-end justify-between gap-6 pt-10">
        <h1 className="display text-6xl">Sus of the Month</h1>
        <div className="flex gap-6">
          <label><span className="label block text-ink/55">Month</span><input type="month" value={month} onChange={(e) => setMonth(e.target.value)} className={field} data-testid="admin-filter-month" /></label>
          <label><span className="label block text-ink/55">Status</span>
            <select value={status} onChange={(e) => setStatus(e.target.value)} className={field} data-testid="admin-filter-status">{STATUSES.map((s) => <option key={s}>{s}</option>)}</select></label>
        </div>
      </div>
      {month && <MonthPanel month={month} />}
      <p className="label mt-8 text-ink/50" data-testid="admin-sub-count">{subs ? `${subs.length} submissions` : "Loading…"}</p>
      <ul className="mt-2">{subs?.map((s) => <Row key={s.id} s={s} reload={load} />)}</ul>
    </>
  );
};

export default function AdminSotm() {
  return <AdminGuard><Panel /></AdminGuard>;
}
